import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  MessageSquare,
  Plus,
  ChevronDown,
  ChevronRight,
  Users,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import * as Dialog from '@radix-ui/react-dialog';
import { api } from '../services/api';
import {
  ouvidoriaService,
  FormularioOuvidoria,
  Ouvidoria,
  CampoOuvidoria,
} from '../services/ouvidoria';

interface CampoTipo {
  id: string;
  nome: string;
  tipo_input: string;
}

interface GrupoOuvidoriaManagerProps {
  grupoId: string;
  isAdmin: boolean;
}

// ─── Modal Criar Formulário ───────────────────────────────────────────────────
interface CriarFormularioModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  grupoId: string;
}

function CriarFormularioModal({ open, onClose, onSuccess, grupoId }: CriarFormularioModalProps) {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [campos, setCampos] = useState<CampoOuvidoria[]>([]);
  const [tiposCampos, setTiposCampos] = useState<CampoTipo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTipos, setLoadingTipos] = useState(false);

  useEffect(() => {
    if (open) {
      setTitulo('');
      setDescricao('');
      setCampos([]);
      carregarTiposCampos();
    }
  }, [open]);

  const carregarTiposCampos = async () => {
    setLoadingTipos(true);
    try {
      const response = await api.get('/campos-tipos');
      setTiposCampos(Array.isArray(response.data) ? response.data : []);
    } catch {
      enqueueSnackbar('Erro ao carregar tipos de campos', { variant: 'error' });
    } finally {
      setLoadingTipos(false);
    }
  };

  const addCampo = () => {
    if (tiposCampos.length === 0) return;
    setCampos((prev) => [
      ...prev,
      { campo: '', id_tipo_campo: tiposCampos[0].id, obrigatorio: false, ordem: prev.length },
    ]);
  };

  const removeCampo = (index: number) => {
    setCampos((prev) => prev.filter((_, i) => i !== index).map((c, i) => ({ ...c, ordem: i })));
  };

  const updateCampo = (index: number, field: keyof CampoOuvidoria, value: string | boolean | number) => {
    setCampos((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim()) {
      enqueueSnackbar('Informe o título do formulário', { variant: 'error' });
      return;
    }
    for (const campo of campos) {
      if (!campo.campo.trim()) {
        enqueueSnackbar('Preencha o nome de todos os campos', { variant: 'error' });
        return;
      }
    }

    setLoading(true);
    try {
      await ouvidoriaService.criarFormulario(grupoId, {
        titulo: titulo.trim(),
        descricao: descricao.trim() || undefined,
        campos,
      });
      enqueueSnackbar('Formulário de ouvidoria criado com sucesso!', { variant: 'success' });
      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao criar formulário', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[9998]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto z-[9999] p-6">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-foreground flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Criar Formulário de Ouvidoria
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: Ouvidoria do Grupo"
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Descrição (opcional)
              </label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descreva o propósito deste formulário de ouvidoria..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none"
              />
            </div>

            {/* Campos */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-foreground">
                  Campos do Formulário
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCampo}
                  disabled={loadingTipos}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" /> Adicionar Campo
                </Button>
              </div>

              {campos.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  Nenhum campo customizado. Adicione campos para coletar informações específicas.
                </p>
              )}

              <div className="space-y-3">
                {campos.map((campo, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 border border-border rounded-md bg-muted/30">
                    <div className="flex-1 space-y-2">
                      <input
                        value={campo.campo}
                        onChange={(e) => updateCampo(index, 'campo', e.target.value)}
                        placeholder="Nome do campo"
                        className="w-full px-2 py-1.5 border border-border rounded bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                      <div className="flex gap-2 items-center">
                        <select
                          value={campo.id_tipo_campo}
                          onChange={(e) => updateCampo(index, 'id_tipo_campo', e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-border rounded bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          {tiposCampos.map((tipo) => (
                            <option key={tipo.id} value={tipo.id}>
                              {tipo.nome} ({tipo.tipo_input})
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1.5 text-sm text-foreground whitespace-nowrap cursor-pointer">
                          <input
                            type="checkbox"
                            checked={campo.obrigatorio}
                            onChange={(e) => updateCampo(index, 'obrigatorio', e.target.checked)}
                            className="rounded"
                          />
                          Obrigatório
                        </label>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCampo(index)}
                      className="text-red-500 hover:text-red-700 mt-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.38_0.15_142)] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Criando...
                  </>
                ) : (
                  'Criar Formulário'
                )}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Linha de Ouvidoria Expandível ────────────────────────────────────────────
function OuvidoriaRow({ ouvidoria }: { ouvidoria: Ouvidoria }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/20 hover:bg-muted/40 transition-colors text-left"
        onClick={() => setExpanded((p) => !p)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          {ouvidoria.anonimo ? (
            <span className="font-medium text-sm text-muted-foreground flex items-center gap-1.5">
              <UserX className="h-3.5 w-3.5" /> Anônimo
            </span>
          ) : (
            <span className="font-medium text-sm text-foreground flex items-center gap-1.5">
              <UserCheck className="h-3.5 w-3.5" /> {ouvidoria.nick_autor || 'Sem identificação'}
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(ouvidoria.criado_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
      </button>

      {expanded && (
        <div className="px-4 py-3 space-y-2 border-t border-border bg-background">
          {[...ouvidoria.campos].sort((a, b) => (a.campoDef?.ordem ?? 0) - (b.campoDef?.ordem ?? 0)).map((campo) => (
            <div key={campo.id} className="grid grid-cols-3 gap-2 text-sm">
              <span className="font-medium text-foreground col-span-1">{campo.campoDef?.campo}:</span>
              <span className="text-muted-foreground col-span-2 break-words whitespace-pre-wrap">{campo.valor || '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Formulário Expandível ────────────────────────────────────────────────────
interface FormularioRowProps {
  formulario: FormularioOuvidoria;
  isAdmin: boolean;
  onToggleStatus: (id: string, ativo: boolean) => void;
  togglingId: string | null;
}

function FormularioRow({ formulario, isAdmin, onToggleStatus, togglingId }: FormularioRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [ouvidorias, setOuvidorias] = useState<Ouvidoria[]>([]);
  const [loadingOuvidorias, setLoadingOuvidorias] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleExpand = async () => {
    if (!expanded && !loaded) {
      setLoadingOuvidorias(true);
      try {
        const data = await ouvidoriaService.listarOuvidorias(formulario.id);
        setOuvidorias(data);
        setLoaded(true);
      } catch {
        enqueueSnackbar('Erro ao carregar ouvidorias', { variant: 'error' });
      } finally {
        setLoadingOuvidorias(false);
      }
    }
    setExpanded((p) => !p);
  };

  return (
    <Card className="overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20 transition-colors"
        onClick={handleExpand}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <div>
            <p className="font-semibold text-foreground text-sm">{formulario.titulo}</p>
            {formulario.descricao && (
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md truncate">{formulario.descricao}</p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5">
              {formulario.campos.length} campo(s) configurado(s)
              {formulario.militarCriou && ` • criado por ${formulario.militarCriou.nick}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          {formulario.ativo ? (
            <Badge className="bg-green-500/20 text-green-600 dark:text-green-400 border border-green-500/30 text-xs">
              Ativo
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">Inativo</Badge>
          )}

          {isAdmin && (
            <button
              onClick={() => onToggleStatus(formulario.id, !formulario.ativo)}
              disabled={togglingId === formulario.id}
              className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              title={formulario.ativo ? 'Inativar' : 'Ativar'}
            >
              {togglingId === formulario.id ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : formulario.ativo ? (
                <ToggleRight className="h-6 w-6 text-green-500" />
              ) : (
                <ToggleLeft className="h-6 w-6" />
              )}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 space-y-3">
          {loadingOuvidorias ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : ouvidorias.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4 flex items-center justify-center gap-2">
              <MessageSquare className="h-4 w-4" /> Nenhuma ouvidoria recebida ainda.
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" /> {ouvidorias.length} ouvidoria(s) recebida(s)
              </p>
              <div className="space-y-2">
                {ouvidorias.map((ouvidoria) => (
                  <OuvidoriaRow key={ouvidoria.id} ouvidoria={ouvidoria} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────
export function GrupoOuvidoriaManager({ grupoId, isAdmin }: GrupoOuvidoriaManagerProps) {
  const [formularios, setFormularios] = useState<FormularioOuvidoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCriarModalOpen, setIsCriarModalOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await ouvidoriaService.listarFormularios(grupoId);
      setFormularios(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao carregar formulários de ouvidoria', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [grupoId]);

  const handleToggleStatus = async (id: string, ativo: boolean) => {
    setTogglingId(id);
    try {
      await ouvidoriaService.alterarStatus(id, ativo);
      enqueueSnackbar(ativo ? 'Formulário ativado!' : 'Formulário inativado!', { variant: 'success' });
      await carregar();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao alterar status', { variant: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {isAdmin && (
          <Button
            onClick={() => setIsCriarModalOpen(true)}
            className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.38_0.15_142)] text-white flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Criar Formulário
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : formularios.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-foreground font-medium">Nenhum formulário de ouvidoria criado ainda.</p>
            {isAdmin && (
              <p className="text-sm text-muted-foreground mt-1">
                Clique em "Criar Formulário" para configurar o canal de ouvidoria do grupo.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {formularios.map((formulario) => (
            <FormularioRow
              key={formulario.id}
              formulario={formulario}
              isAdmin={isAdmin}
              onToggleStatus={handleToggleStatus}
              togglingId={togglingId}
            />
          ))}
        </div>
      )}

      <CriarFormularioModal
        open={isCriarModalOpen}
        onClose={() => setIsCriarModalOpen(false)}
        onSuccess={() => {
          setIsCriarModalOpen(false);
          carregar();
        }}
        grupoId={grupoId}
      />
    </div>
  );
}
